import type { ComponentType, KeyboardEvent } from 'react';
import {
  AlertTriangle,
  Bold,
  BookOpen,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Image as ImageIcon,
  Info,
  Italic,
  Lightbulb,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  ShieldAlert,
  Subscript,
  Superscript,
  Table as TableIcon,
  TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { EditorSelection } from '@/lib/asciidocEditing';
import {
  admonitionTemplate,
  codeBlockTemplate,
  insertBlockAtCursor,
  insertLink,
  insertWikilink,
  quoteTemplate,
  setHeadingLevel,
  tableTemplate,
  thematicBreakTemplate,
  toggleInlineWrap,
  toggleLinePrefix,
  toggleOrderedList,
} from '@/lib/asciidocEditing';

interface AsciiDocToolbarProps {
  applyEdit: (transform: (sel: EditorSelection) => EditorSelection) => void;
}

interface ToolbarAction {
  label: string;
  icon: ComponentType<{ className?: string }>;
  run: (sel: EditorSelection) => EditorSelection;
  shortcut?: string;
}

const textActions: ToolbarAction[] = [
  { label: 'Bold', icon: Bold, run: (s) => toggleInlineWrap(s, '*', 'strong text'), shortcut: 'Ctrl+B' },
  { label: 'Italic', icon: Italic, run: (s) => toggleInlineWrap(s, '_', 'italic text'), shortcut: 'Ctrl+I' },
  { label: 'Monospace', icon: Code, run: (s) => toggleInlineWrap(s, '`', 'code'), shortcut: 'Ctrl+E' },
  { label: 'Highlight', icon: Highlighter, run: (s) => toggleInlineWrap(s, '#', 'highlighted text') },
  { label: 'Superscript', icon: Superscript, run: (s) => toggleInlineWrap(s, '^', 'sup') },
  { label: 'Subscript', icon: Subscript, run: (s) => toggleInlineWrap(s, '~', 'sub') },
];

// Section content starts headings at level 2 (`==`) rather than level 1
// (`=`), since a bare `=` on the first line would be parsed as the
// document title rather than a section heading.
const headingActions: ToolbarAction[] = [
  { label: 'Heading (==)', icon: Heading1, run: (s) => setHeadingLevel(s, 2) },
  { label: 'Subheading (===)', icon: Heading2, run: (s) => setHeadingLevel(s, 3) },
  { label: 'Sub-subheading (====)', icon: Heading3, run: (s) => setHeadingLevel(s, 4) },
];

const listActions: ToolbarAction[] = [
  { label: 'Bulleted list', icon: List, run: (s) => toggleLinePrefix(s, '* ') },
  { label: 'Numbered list', icon: ListOrdered, run: toggleOrderedList },
  { label: 'Blockquote', icon: Quote, run: (s) => insertBlockAtCursor(s, quoteTemplate) },
];

const admonitionActions: ToolbarAction[] = [
  { label: 'Note', icon: Info, run: (s) => insertBlockAtCursor(s, admonitionTemplate('NOTE')) },
  { label: 'Tip', icon: Lightbulb, run: (s) => insertBlockAtCursor(s, admonitionTemplate('TIP')) },
  { label: 'Important', icon: ShieldAlert, run: (s) => insertBlockAtCursor(s, admonitionTemplate('IMPORTANT')) },
  { label: 'Warning', icon: TriangleAlert, run: (s) => insertBlockAtCursor(s, admonitionTemplate('WARNING')) },
  { label: 'Caution', icon: AlertTriangle, run: (s) => insertBlockAtCursor(s, admonitionTemplate('CAUTION')) },
];

const insertActions: ToolbarAction[] = [
  { label: 'Link', icon: LinkIcon, run: (s) => insertLink(s, false), shortcut: 'Ctrl+K' },
  { label: 'Image', icon: ImageIcon, run: (s) => insertLink(s, true) },
  { label: 'Wikilink [[…]]', icon: BookOpen, run: insertWikilink, shortcut: 'Ctrl+[' },
  { label: 'Code block', icon: Code, run: (s) => insertBlockAtCursor(s, codeBlockTemplate) },
  { label: 'Table', icon: TableIcon, run: (s) => insertBlockAtCursor(s, tableTemplate) },
  { label: 'Horizontal rule', icon: Minus, run: (s) => insertBlockAtCursor(s, thematicBreakTemplate) },
];

function ToolbarButton({ action, applyEdit }: { action: ToolbarAction; applyEdit: AsciiDocToolbarProps['applyEdit'] }) {
  const Icon = action.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => applyEdit(action.run)}
          aria-label={action.label}
        >
          <Icon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {action.label}
        {action.shortcut ? ` (${action.shortcut})` : ''}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Formatting toolbar for the AsciiDoc markup editor (used by NKBIP-01
 * publication sections).
 *
 * Unstyled container — the caller (`AsciiDocEditor`) owns the
 * surrounding border/background/rounding, since the toolbar shares that
 * bar with the view-mode toggle.
 */
export function AsciiDocToolbar({ applyEdit }: AsciiDocToolbarProps) {
  return (
    <div className="flex flex-nowrap items-center gap-0.5">
      {headingActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {textActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {listActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {admonitionActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
      <Separator orientation="vertical" className="mx-1 h-5" />
      {insertActions.map((action) => (
        <ToolbarButton key={action.label} action={action} applyEdit={applyEdit} />
      ))}
    </div>
  );
}

/** Keyboard shortcuts for the most common formatting actions, plus
    focus/save options — same shape as the Djot toolbar's. */
export function handleAsciiDocEditorKeyDown(
  e: KeyboardEvent<HTMLTextAreaElement>,
  applyEdit: AsciiDocToolbarProps['applyEdit'],
  options?: { onToggleFocus?: () => void; onSave?: () => void },
): void {
  const mod = e.metaKey || e.ctrlKey;
  if (!mod) return;

  if (options?.onSave && e.key.toLowerCase() === 's' && !e.shiftKey) {
    e.preventDefault();
    options.onSave();
    return;
  }
  if (options?.onToggleFocus && e.key.toLowerCase() === 'f' && e.shiftKey) {
    e.preventDefault();
    options.onToggleFocus();
    return;
  }

  switch (e.key.toLowerCase()) {
    case 'b':
      e.preventDefault();
      applyEdit((s) => toggleInlineWrap(s, '*', 'strong text'));
      break;
    case 'i':
      e.preventDefault();
      applyEdit((s) => toggleInlineWrap(s, '_', 'italic text'));
      break;
    case 'e':
      e.preventDefault();
      applyEdit((s) => toggleInlineWrap(s, '`', 'code'));
      break;
    case 'k':
      e.preventDefault();
      applyEdit((s) => insertLink(s, false));
      break;
    case '[':
      e.preventDefault();
      applyEdit(insertWikilink);
      break;
    default:
      break;
  }
}
